/* ============================================================================
   Dashboard Configuration — presentational controls

   Split out of DashboardConfig.tsx (which sits at the 700-line limit). Holds the
   role-list row plus the per-card controls:
     • TriStateControl — Inherit / On / Off (admin role config).
     • ToggleSwitch    — binary On/Off (restricted users' personal preference).
   CardToggle renders whichever the page asks for via `mode`.
   ========================================================================== */

import { ChevronRight, Loader2, Info, CornerDownRight } from "lucide-react";
import type { DashboardCard } from "./dashboardConfigData";

/* A widget's pin state. Restricted users only ever use "on"/"off". */
export type WState = "inherit" | "on" | "off";

interface Role {
  id: string;
  name?: string;
  displayName: string;
  description?: string;
}

/* -- Role row in the left list --------------------------------------------- */
export function RoleListItem({
  role, active, count, onClick,
}: {
  role: Role;
  active: boolean;
  count: number;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`group flex w-full items-center gap-3 rounded-xl border p-3 text-left transition-all ${
        active
          ? "border-indigo-500/50 bg-indigo-500/[0.06]"
          : "border-border bg-card hover:border-indigo-400/40 hover:bg-foreground/[0.02]"
      }`}
    >
      <div
        className={`flex size-9 shrink-0 items-center justify-center rounded-lg text-xs font-bold transition-colors ${
          active ? "bg-indigo-600 text-white" : "bg-foreground/[0.06] text-muted-foreground"
        }`}
      >
        {role.displayName.slice(0, 2)}
      </div>
      <div className="min-w-0 flex-1">
        <span className="block truncate text-sm font-semibold text-foreground">{role.displayName}</span>
        <span className="block truncate text-[11px] text-muted-foreground">
          {count} card{count === 1 ? "" : "s"} enabled
        </span>
      </div>
      <ChevronRight
        className={`size-4 shrink-0 transition-colors ${
          active ? "text-indigo-400" : "text-muted-foreground/50 group-hover:text-muted-foreground"
        }`}
      />
    </button>
  );
}

/* -- A single card row with its control ------------------------------------ */
export function CardToggle({
  card, mode, state, saving, disabled = false, inheritedFrom, onChange,
}: {
  card: DashboardCard;
  mode: "tristate" | "binary";
  state: WState;
  saving: boolean;
  disabled?: boolean;
  inheritedFrom?: string;
  onChange: (next: WState) => void;
}) {
  const Icon = card.icon;
  const on = state === "on";
  return (
    <div
      className={`flex items-center gap-3 rounded-lg border border-l-[3px] p-3 transition-all ${
        on
          ? "border-border border-l-emerald-500 bg-background"
          : "border-border border-l-border bg-background"
      }`}
    >
      <div
        className="flex size-9 shrink-0 items-center justify-center rounded-lg"
        style={{ background: `${card.accent}1f` }}
      >
        <Icon className="size-4" style={{ color: card.accent }} />
      </div>
      <div className="flex min-w-0 flex-1 items-center gap-1.5">
        <span className="truncate text-sm font-semibold text-foreground">{card.title}</span>
        {/* info icon — hover to reveal the card's full description in a tooltip */}
        <span className="group/info relative inline-flex shrink-0">
          <Info className="size-3.5 cursor-default text-muted-foreground/70 transition-colors hover:text-foreground" />
          <span
            role="tooltip"
            className="pointer-events-none absolute bottom-full left-1/2 z-30 mb-2 w-[260px] -translate-x-1/2 whitespace-normal break-words rounded-md border border-border bg-popover px-3 py-2 text-xs font-medium leading-relaxed text-foreground opacity-0 shadow-lg transition-opacity duration-150 group-hover/info:opacity-100"
          >
            {card.description}
            {/* arrow pointing down to the (i) icon */}
            <span className="absolute left-1/2 top-full size-3 -translate-x-1/2 -translate-y-1/2 rotate-45 border-b border-r border-border bg-popover" />
          </span>
        </span>
      </div>

      {/* Inherited-from badge — cascaded from an ancestor group/org. */}
      {inheritedFrom && state === "inherit" && (
        <span
          title={`Inherited from ${inheritedFrom}`}
          className="flex shrink-0 items-center gap-1 rounded bg-sky-500/10 px-1.5 py-0.5 text-[10px] font-medium text-sky-600 dark:text-sky-400"
        >
          <CornerDownRight className="size-2.5 shrink-0" />
          <span className="max-w-[110px] truncate">{inheritedFrom}</span>
        </span>
      )}

      <div className="flex shrink-0 items-center">
        {mode === "tristate" ? (
          <TriStateControl
            value={state}
            saving={saving}
            disabled={disabled}
            onChange={onChange}
            label={card.title}
          />
        ) : (
          <ToggleSwitch
            on={on}
            saving={saving}
            disabled={disabled}
            onToggle={() => onChange(on ? "off" : "on")}
            label={`${on ? "Disable" : "Enable"} ${card.title}`}
          />
        )}
      </div>
    </div>
  );
}

/* -- Inherit / On / Off segmented control (admin role config) -------------- */
const TRISTATE_OPTIONS: { value: WState; label: string; activeClass: string }[] = [
  { value: "inherit", label: "Inherit", activeClass: "bg-sky-500/15 text-sky-600 dark:text-sky-400" },
  { value: "on",      label: "On",      activeClass: "bg-emerald-500 text-white" },
  { value: "off",     label: "Off",     activeClass: "bg-rose-500/15 text-rose-600 dark:text-rose-400" },
];

export function TriStateControl({
  value, saving, disabled = false, onChange, label,
}: {
  value: WState;
  saving: boolean;
  disabled?: boolean;
  onChange: (next: WState) => void;
  label: string;
}) {
  return (
    <div
      role="radiogroup"
      aria-label={`${label} visibility`}
      className={`inline-flex items-center overflow-hidden rounded-lg border border-border ${
        saving || disabled ? "opacity-60" : ""
      }`}
    >
      {TRISTATE_OPTIONS.map((opt, i) => {
        const selected = value === opt.value;
        return (
          <button
            key={opt.value}
            type="button"
            role="radio"
            aria-checked={selected}
            aria-label={`${opt.label} ${label}`}
            title={`${opt.label} ${label}`}
            disabled={saving || disabled}
            onClick={() => onChange(opt.value)}
            className={`relative px-2.5 py-1.5 text-[11px] font-bold tracking-wide transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500/40 ${
              i > 0 ? "border-l border-border" : ""
            } ${
              selected ? opt.activeClass : "text-muted-foreground/80 hover:text-foreground"
            } ${saving || disabled ? "cursor-not-allowed" : ""}`}
          >
            {selected && saving && opt.value === value ? (
              <Loader2 className="size-3 animate-spin" />
            ) : (
              opt.label
            )}
          </button>
        );
      })}
    </div>
  );
}

/* -- Reusable binary On/Off switch (restricted users) ---------------------- */
export function ToggleSwitch({
  on, saving, disabled = false, onToggle, label,
}: {
  on: boolean;
  saving: boolean;
  disabled?: boolean;
  onToggle: () => void;
  label: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      aria-label={label}
      title={label}
      onClick={onToggle}
      disabled={saving || disabled}
      className={`relative inline-flex h-7 w-[64px] shrink-0 items-center rounded-full border transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500/40 ${
        saving ? "disabled:cursor-wait" : "disabled:cursor-not-allowed disabled:opacity-60"
      } ${
        on
          ? "border-emerald-400/50 bg-emerald-500"
          : "border-border bg-foreground/15"
      }`}
    >
      {/* status label sits INSIDE the track */}
      <span
        className={`pointer-events-none absolute text-[10px] font-bold tracking-wide transition-all duration-200 ${
          on ? "left-2.5 text-white" : "right-2.5 text-muted-foreground/80"
        }`}
      >
        {on ? "On" : "Off"}
      </span>
      {/* sliding knob */}
      <span
        className={`pointer-events-none absolute left-1 flex size-5 items-center justify-center rounded-full bg-white shadow-sm transition-transform duration-200 ${
          on ? "translate-x-[36px]" : "translate-x-0"
        }`}
      >
        {saving && <Loader2 className="size-3 animate-spin text-foreground/40" />}
      </span>
    </button>
  );
}
